// Documents API
import api, { type AxiosResponse } from './client';
import type {
  DocumentCategory,
  Document,
  DocumentDownload,
  DocumentStats,
  CategoryWithDocuments,
  QueryParams,
} from './types';

export const documentsAPI = {
  // Categories
  getCategories: (params?: QueryParams): Promise<AxiosResponse<DocumentCategory[]>> =>
    api.get('/documents/categories/', { params }),

  getCategory: (id: number): Promise<AxiosResponse<DocumentCategory>> =>
    api.get(`/documents/categories/${id}/`),

  createCategory: (data: Partial<DocumentCategory>): Promise<AxiosResponse<DocumentCategory>> =>
    api.post('/documents/categories/', data),

  updateCategory: (id: number, data: Partial<DocumentCategory>): Promise<AxiosResponse<DocumentCategory>> =>
    api.patch(`/documents/categories/${id}/`, data),

  deleteCategory: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/documents/categories/${id}/`),

  getCategoryDocuments: (id: number): Promise<AxiosResponse<Document[]>> =>
    api.get(`/documents/categories/${id}/documents/`),

  // Documents
  getDocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: Document[] }>> =>
    api.get('/documents/documents/', { params }),

  getDocument: (id: number): Promise<AxiosResponse<Document>> =>
    api.get(`/documents/documents/${id}/`),

  createDocument: (data: FormData): Promise<AxiosResponse<Document>> =>
    api.post('/documents/documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  updateDocument: (id: number, data: FormData | Partial<Document>): Promise<AxiosResponse<Document>> =>
    api.patch(`/documents/documents/${id}/`, data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
    }),

  deleteDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/documents/documents/${id}/`),

  downloadDocument: (id: number): Promise<AxiosResponse<{ file_url: string; title: string; file_size: string }>> =>
    api.post(`/documents/documents/${id}/download/`),

  // CR-5331: getPreviewUrl removed. The previous implementation embedded
  // the permanent JWT in the URL (CWE-200) AND was rejected by the
  // backend's preview endpoint (which only honours u/exp/sig signed
  // params). Use downloadDocument(id) to mint a short-lived signed link
  // and open response.data.file_url instead.

  getDocumentsByCategory: (params?: QueryParams): Promise<AxiosResponse<CategoryWithDocuments[]>> =>
    api.get('/documents/documents/by_category/', { params }),

  getDocumentStats: (params?: QueryParams): Promise<AxiosResponse<DocumentStats>> =>
    api.get('/documents/documents/stats/', { params }),

  // CR-5330: Audit history feed for the document details popup. The
  // backend's history action combines the upload event, downloads and
  // read-receipts into a single Changelog-shaped feed (newest first).
  getDocumentHistory: (id: number): Promise<AxiosResponse<DocumentHistoryEntry[]>> =>
    api.get(`/documents/documents/${id}/history/`),

  // CR-5328: Version-history endpoints. The current file lives on
  // Document; older snapshots live in DocumentVersion. uploadNewVersion
  // snapshots the previous file then replaces with the new one.
  getDocumentVersions: (id: number): Promise<AxiosResponse<DocumentVersionEntry[]>> =>
    api.get(`/documents/documents/${id}/versions/`),

  uploadNewVersion: (id: number, data: FormData): Promise<AxiosResponse<Document>> =>
    api.post(`/documents/documents/${id}/upload_version/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  downloadVersion: (id: number, versionId: number): Promise<AxiosResponse<{ file_url: string; title: string; version_number: string; file_size: string }>> =>
    api.post(`/documents/documents/${id}/versions/${versionId}/download/`),

  // CR-5329: Record a Read & Accept confirmation. Idempotent —
  // re-clicking is a no-op. Returns the refreshed Document with
  // has_accepted=true / accepted_at populated for the caller.
  acceptDocument: (id: number): Promise<AxiosResponse<Document>> =>
    api.post(`/documents/documents/${id}/accept/`),

  // Downloads
  getDownloads: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: DocumentDownload[] }>> =>
    api.get('/documents/downloads/', { params }),
};

// CR-5330: Shape returned by the documents history endpoint. Mirrors the
// ChangelogEntry interface in components/common/Changelog.tsx (with id
// loosened to string|number — this endpoint synthesises ids like
// 'upload-12' / 'download-44' from heterogeneous tables).
export interface DocumentHistoryEntry {
  id: string | number;
  change_date: string;
  changed_by: string;
  changed_by_company?: string | null;
  field_changed: string;
  old_value: string | null;
  new_value: string;
  event_type?: string;
}

// CR-5328: Shape returned by the version-history endpoint. Mirrors the
// DocumentVersionSerializer fields.
export interface DocumentVersionEntry {
  id: number;
  document: number;
  version_number: string;
  notes: string;
  uploaded_by: number | null;
  uploaded_by_name: string;
  uploaded_at: string;
  formatted_file_size: string;
  file_extension: string;
  mime_type: string;
}
